import './ReviewView.scss'
import React, { useState, useReducer } from 'react'
import { useTranslation } from 'i18n'
import { Modal, Checkbox } from '@mui/material'
import * as Yup from 'yup'
import { Formik, Field } from 'formik'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import ConfirmationModal from 'components/forms/confirmation-modal/ConfirmationModal'
import { generateTokenService, validateTokenService } from 'services/token'
import { TurnstileWidget } from 'components/TurnstileWidget'
import { useUserContext } from 'hooks/useUserContext'

import Check from 'images/inline/receipt/check2.svg'

import {
  AddressModal,
  NameFields,
  AddressFields,
  returnDynamicValidation,
  validationSchema,
  renderTextInput,
  FormikRadioGroup2,
} from 'components/forms'

import { addressLookup, addressValidate } from 'services/address'
import { isObjectKeysNotEmpty } from 'util/objectNotEmpty'

const dynamicValidation = reward => {
  let smartValidation = returnDynamicValidation(
    validationSchema,
    !!reward.requireName,
    !!reward.requireAddress,
    !!reward.requirePhoneNumber,
    reward.confirmations,
  )
  return Yup.object().shape(smartValidation)
}

const rewardReducer = (state, action) => {
  switch (action.type) {
    case 'changeReward':
      // console.log(`changeReward`, state, action)
      let reward = action.reward

      let smartValidation = dynamicValidation(reward)

      return {
        nameRequired: reward.requireName,
        phoneRequired: reward.requirePhoneNumber,
        addressRequired: reward.requireAddress,
        hasAddressValidation: reward.hasAddressValidation === true,
        validationSchema: smartValidation,
        selectedReward: reward,
      }
    default:
      return state
  }
}

const SubmissionForm = ({
  breakPermalink,
  communicationChannel,
  children,
  editMySubmission,
  instructions = [],
  nonFieldErrors = [],
  onFormSubmission,
  reviewListHeader,
  rewardOptions = [],
  stepType,
  stepId,
  isEmbed = false,
}) => {
  const { t } = useTranslation(['receipt', 'common'])
  //initialize form state with reducer
  let initialState = {
    nameRequired: false,
    phoneRequired: false,
    addressRequired: false,
    hasAddressValidation: false,
    validationSchema: Yup.object().shape(validationSchema),
  }

  if (rewardOptions && rewardOptions.length > 0) {
    initialState = {
      selectedReward: rewardOptions[0],
      emailFieldMode: rewardOptions[0].emailFieldMode,
      nameRequired: rewardOptions[0].requireName,
      phoneRequired: rewardOptions[0].requirePhoneNumber,
      addressRequired: rewardOptions[0].requireAddress,
      hasAddressValidation: rewardOptions[0].hasAddressValidation === true,
      validationSchema: dynamicValidation(rewardOptions[0]),
    }
  }

  const [showSmsConfirmationModal, setShowSmsConfirmationModal] =
    useState(false)
  const [addressSuggestionModal, setAddressSuggestionModal] = useState(false)
  const [apiError, setApiError] = useState(false)
  const [suggestedAddress, setSuggestionAddress] = useState({})
  const [invalidAddress, setInvalidAddress] = useState(false)
  const [state, dispatch] = useReducer(rewardReducer, initialState)
  const [doubleClickTimeoutActive, setDoubleClickTimeoutActive] =
    useState(false)
  const profile = useUserContext()

  const formRewardOptions = rewardOptions.map(option => ({
    label: option.title,
    value: option.id,
    key: option.id,
  }))

  const initialValues = {
    email: isEmbed ? '' : profile ? profile.email : '',
    phoneNumber: '',
    termsAccepted: true,
    eighteenOrOver: true,
    address1: '',
    address2: '',
    city: '',
    state: '',
    postalCode: '',
    firstName: '',
    lastName: '',
    type: communicationChannel === 'sms' ? 'SMS' : 'EMAIL',
    turnstileToken: '',
    turnstileTokenType: 'visible',
  }
  if (rewardOptions && rewardOptions.length > 0) {
    initialValues.rewardId = formRewardOptions[0].value
  }

  const confirmations = state?.selectedReward?.confirmations

  const handleFormatValues = values => {
    const formattedConfirmations = (confirmations ?? []).reduce(
      (acc, confirmation) => {
        acc[confirmation.id] = values[confirmation.id] ?? false
        delete values[confirmation.id]
        return acc
      },
      {},
    )
    return { ...values, confirmations: formattedConfirmations }
  }

  const handleRewardSelectionChange = e => {
    let selecteReward = rewardOptions.find(
      option => option.id === e.target.value,
    )
    dispatch({ type: 'changeReward', reward: selecteReward })
  }

  const handleAddressSuggestionModal = () => {
    setAddressSuggestionModal(!addressSuggestionModal)
  }

  const lookupAddress = async values => {
    if (!state.hasAddressValidation) return
    setInvalidAddress(false)
    let addressValues = {
      address1: values.address1 || '',
      address2: values.address2,
      city: values.city,
      state: values.state,
      zip: values.postalCode,
      zip2: '',
    }

    const isAddressNotEmpty = isObjectKeysNotEmpty({
      address1: values.address1,
      city: values.city,
      state: values.state,
      zip: values.postalCode,
    })
    if (isAddressNotEmpty) {
      await addressLookup(addressValues)
        .then(res => {
          const addressSuggestion = res.detail
          setSuggestionAddress(addressSuggestion)
          return res
        })
        .then(data => {
          setAddressSuggestionModal(true)
          console.log('data from USPS: ', data.detail)
        })
        .catch(err => console.log(err))
    }
  }

  const validateAddress = async values => {
    setInvalidAddress(false)
    let addressValues = {
      address1: values.address1 || '',
      address2: values.address2,
      city: values.city,
      state: values.state,
      zip: values.postalCode,
      zip2: '',
    }

    const isAddressNotEmpty = isObjectKeysNotEmpty({
      address1: values.address1,
      city: values.city,
      state: values.state,
      zip: values.postalCode,
    })

    if (isAddressNotEmpty) {
      try {
        const res = await addressValidate(addressValues)
        const { openSuggestionModal, suggestionAddress } = res
        if (openSuggestionModal) {
          setSuggestionAddress(suggestionAddress)
          setAddressSuggestionModal(!addressSuggestionModal)
          return false
        }
        return true
      } catch (err) {
        setInvalidAddress(true)
        setAddressSuggestionModal(!addressSuggestionModal)
        return false
      }
    }
    return true
  }

  const onSendToken = async ({
    email,
    phoneNumber,
    type,
    firstName,
    lastName,
    tokenSourceId,
    permalink,
  }) => {
    await generateTokenService({
      tokenSourceId: tokenSourceId || '',
      tokenSource: 'RECEIPT_UPLOAD',
      phoneNumber,
      type,
      permalink,
      email,
      firstName,
      lastName,
    })
  }

  const handleReceiptSubmit = async values => {
    if (state.hasAddressValidation) {
      const isAddressValid = await validateAddress(values)
      if (!isAddressValid) return
    }
    if (values.type === 'SMS') {
      const { firstName, lastName, email } = values
      try {
        await onSendToken({
          stepType,
          email,
          firstName,
          lastName,
          stepId,
          type: values.type,
          phoneNumber: values.phoneNumber.replace(/\D/g, ''),
          breakPermalink,
        })
        setShowSmsConfirmationModal(true)
      } catch {
        console.warn(`BR-9375104`)
        setApiError(t('receipt:error.tokenGeneration'))
      }
      return
    } else {
      onFormSubmission(handleFormatValues(values))
    }
  }

  return (
    <Formik
      validateOnBlur={false}
      validateOnChange={false}
      enableReinitialize={true}
      initialValues={initialValues}
      validationSchema={state.validationSchema}
      onSubmit={async values => {
        if (doubleClickTimeoutActive === false) {
          setDoubleClickTimeoutActive(true)

          console.log(`onSubmit`, values)
          await handleReceiptSubmit(values)

          setTimeout(function () {
            setDoubleClickTimeoutActive(false)
          }, 2000)
        }
      }}
    >
      {props => {
        const {
          values,
          handleSubmit,
          setValues,
          setFieldValue,
          errors,
          touched,
          isValidating,
          isSubmitting,
        } = props
        // console.log(`formik`, props)

        const handleAddressSuggestionModalAccepted = suggestedAddress => {
          setAddressSuggestionModal(false)
          setValues({
            ...values,
            address1: suggestedAddress.address1 ?? values.address1 ?? '',
            address2: suggestedAddress.address2 ?? values.address2 ?? '',
            city: suggestedAddress.city ?? values.city ?? '',
            state: suggestedAddress.state ?? values.state ?? '',
            postalCode: suggestedAddress.zip ?? values.postalCode ?? '',
          })
        }

        const handleSmsConfirmationModal = ({ values, confirmed }) => {
          setShowSmsConfirmationModal(false)
          if (confirmed) {
            onFormSubmission(handleFormatValues(values))
          }
        }

        return (
          <form
            id="receipt-review-form"
            className={`receipt-review-form`}
            onSubmit={handleSubmit}
            key={`formik-review-form`}
          >
            <ul className="form__elements" key={`formik-field-elements`}>
              {rewardOptions.length > 1 && (
                <li className="form__element" key={`formik-field-1`}>
                  <div className="receipt-reward-options">
                    <span className="field-title">
                      {t('receipt:selectRewardLabel')}
                    </span>
                    <FormikRadioGroup2
                      name="rewardId"
                      setFieldValue={setFieldValue}
                      options={formRewardOptions}
                      value={values.rewardId}
                      defaultValue={formRewardOptions[0].value}
                      onChange={handleRewardSelectionChange}
                    />
                    {errors.rewardId && (
                      <p className="error">{errors.rewardId}</p>
                    )}
                  </div>
                </li>
              )}

              {(state.addressRequired || state.hasAddressValidation) && (
                <Modal
                  open={addressSuggestionModal}
                  onClose={handleAddressSuggestionModal}
                >
                  <div
                    style={{
                      backgroundColor: 'white',
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                    }}
                  >
                    <AddressModal
                      notValid={invalidAddress}
                      values={values}
                      onClose={handleAddressSuggestionModal}
                      onAccept={handleAddressSuggestionModalAccepted}
                      suggestedAddress={suggestedAddress}
                    />
                  </div>
                </Modal>
              )}
              <Modal
                open={showSmsConfirmationModal}
                onClose={() => setShowSmsConfirmationModal(false)}
              >
                <ConfirmationModal
                  breakPermalink={breakPermalink}
                  stepType={'RECEIPT_UPLOAD'}
                  stepId={stepId}
                  values={{
                    ...values,
                    type: 'SMS',
                    phoneNumber: values.phoneNumber.replace(/\D/g, ''),
                  }}
                  validateTokenService={validateTokenService}
                  onConfirm={handleSmsConfirmationModal}
                  onSendToken={onSendToken}
                />
              </Modal>
              <li
                className="form__element form__element-email"
                key={`formik-field-0`}
              >
                <Field
                  name={`email`}
                  type={state.emailFieldMode === 'hidden' ? 'hidden' : 'email'}
                  component={renderTextInput}
                  variant="filled"
                  label={t('receipt:form.emailLabelRequired')}
                  placeholder={t('receipt:form.emailPlaceholder')}
                  fullWidth={true}
                  //   defaultValue={}
                  disabled={state.emailFieldMode === 'disabled'}
                />
                {errors.email && <p className="error">{errors.email}</p>}
              </li>

              {state.nameRequired && <NameFields {...props} />}

              {state.phoneRequired && (
                <li className="form__element" key={`phoneNumber`}>
                  <div className="input-row">
                    <Field
                      name={`phoneNumber`}
                      type="phoneNumber"
                      component={renderTextInput}
                      variant="filled"
                      label={t('receipt:form.phoneLabelRequired')}
                      placeholder={t('receipt:form.phonePlaceholder')}
                    />
                  </div>
                  <div className="input-row">
                    {errors.phoneNumber && (
                      <p className="error">{errors.phoneNumber}</p>
                    )}
                  </div>
                </li>
              )}

              {(state.addressRequired || state.hasAddressValidation) && (
                <AddressFields lookupAddress={lookupAddress} {...props} />
              )}
              <li
                className="form__element image-upload-list-parent"
                key={`formik-field-children`}
              >
                {children && children.length === 0 && (
                  <p className="error">{t('receipt:error.noReceiptImages')}</p>
                )}
                {children}
                {/* "fake" (e.g. not actually a formik field) validation error if uplaods were somehow deleted. submit button is disabled so we don't need to put this through formik */}
              </li>

              <li className="form__element" key={`formik-field-confirm-items`}>
                <div className={'confirm-items-dialogue'}>
                  {reviewListHeader && <h3>{reviewListHeader}</h3>}
                  <ul className="receipt-review-checkoff">
                    {instructions.map((instruction, index) => {
                      return (
                        instruction.desc && (
                          <li key={`form-checkoff-item-${index}`}>
                            <Check />
                            <div className="receipt-review-checkoff-inside">
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: instruction.desc,
                                }}
                              ></span>
                            </div>
                          </li>
                        )
                      )
                    })}
                  </ul>
                </div>
              </li>

              {communicationChannel === 'either' && (
                <div className="communication-method">
                  <span className="title">
                    {t('receipt:communication.title')}
                  </span>
                  <div className="copy">
                    {t('receipt:communication.description')}
                  </div>
                  <div className="radio">
                    <RadioGroup
                      row
                      name="row-radio-buttons-group"
                      value={values.type}
                      onChange={e => setFieldValue('type', e.target.value)}
                    >
                      <FormControlLabel
                        style={{ paddingRight: '100px' }}
                        value="EMAIL"
                        control={<Radio />}
                        label={t('receipt:communication.email')}
                      />
                      <FormControlLabel
                        value="SMS"
                        control={<Radio />}
                        label={t('receipt:communication.textMessage')}
                      />
                    </RadioGroup>
                  </div>
                </div>
              )}

              {state.selectedReward &&
                confirmations &&
                confirmations.map((confirmation, i) => {
                  let checkboxKey = `${confirmation.id}`
                  // console.log(checkboxKey, confirmation)
                  return (
                    <li className="form__element checkbox-row" key={i}>
                      <div className="input-row">
                        <Field
                          type="checkbox"
                          value={true}
                          component={Checkbox}
                          name={checkboxKey}
                          touched={touched[checkboxKey] && true ? 'true' : ''}
                          onChange={e => {
                            setFieldValue(checkboxKey, e.target.checked)
                          }}
                        />

                        <label
                          htmlFor={checkboxKey}
                          dangerouslySetInnerHTML={{
                            __html:
                              '' +
                              (confirmation.required ? '* ' : '') +
                              confirmation.text,
                          }}
                        ></label>
                      </div>

                      <div className="input-row">
                        {errors[checkboxKey] && (
                          <p className="error">{errors[checkboxKey]}</p>
                        )}
                      </div>
                    </li>
                  )
                })}
              {nonFieldErrors.length > 0 && (
                <li
                  className="form__submit_errors"
                  key={`formik-submit-errors`}
                >
                  {nonFieldErrors.map(error => {
                    return <p className="error">{error}</p>
                  })}
                </li>
              )}
              {invalidAddress && (
                <li className="form__usps_errors" key={`formik-usps-errors`}>
                  <p className="error">
                    {t('receipt:error.invalidMailingAddress')}
                  </p>
                </li>
              )}
              {apiError && (
                <li className="form__usps_errors" key={`formik-usps-errors`}>
                  <p className="error">{apiError}</p>
                </li>
              )}
              <li className="form__turnstile" key={`formik-field-turnstile`}>
                <TurnstileWidget
                  mode="visible"
                  onToken={token => setFieldValue('turnstileToken', token)}
                />
              </li>
              <li className="form__submit-wrapper" key={`formik-field-cta`}>
                <div className={`receipt-button-container`}>
                  <button
                    className="btn themed-button-secondary"
                    type="button"
                    onClick={() => editMySubmission()}
                  >
                    {t('receipt:editSubmission')}
                  </button>
                  <input
                    className="btn themed-button"
                    type="submit"
                    value={
                      doubleClickTimeoutActive
                        ? t('common:submitting')
                        : t('common:submit')
                    }
                    disabled={doubleClickTimeoutActive || !values.turnstileToken}
                    aria-busy={doubleClickTimeoutActive}
                  />
                </div>
              </li>
            </ul>
          </form>
        )
      }}
    </Formik>
  )
}

const ReviewView = props => {
  let headline = props.headerTitle
  let introCopy = props.headerSubtitle
  return (
    <div className="receipt-view review-view">
      <div className={`receipt-view-header`}>
        {headline && (
          <h2
            className="receipt-header-headline step-content__title"
            dangerouslySetInnerHTML={{ __html: headline }}
          />
        )}
        {introCopy && (
          <div
            className="receipt-header-desc"
            dangerouslySetInnerHTML={{ __html: introCopy }}
          />
        )}
      </div>
      <div className={`receipt-view-content`}>
        <SubmissionForm {...props} editMySubmission={props.editMySubmission} />
      </div>
    </div>
  )
}
export { ReviewView, SubmissionForm }
